import { Box, Button, useMediaQuery } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { Formik, FormikHelpers } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast, ToastContent } from 'react-toastify';

import {
  AppWrapper,
  Card,
  DrawerItem,
  ShipsFromNote,
  SummaryCard,
  Title,
  VendorLine,
  ViewDetailsSidebar,
} from '../../components/_mui';
import { trackFormSubmit } from '../../constants/analytics';
import {
  getYourPicksTitle,
  MARKETPLACE_SHIPPING_COUNTRY_LABEL,
  PICKED_FOR_YOU_TITLE,
} from '../../constants/marketplace';
import { FORMS_ENUM } from '../../constants/shipping';
import { CARD_MAX_WIDTH, RAIL_COLLAPSE_BREAKPOINT } from '../../constants/ui';
import useAnalytics from '../../hooks/useAnalytics';
import useWindowSize from '../../hooks/useWindowSize';
import { updateRequestAction } from '../../store/actions/form';
import { selectMappedPickedItems, selectSelectionCampaignName } from '../../store/selectors/derived';
import { selectInitialAddress, selectIsDigital, selectTaxRequiredCountries } from '../../store/selectors/init';
import {
  selectAllowedCountries,
  selectClaimSource,
  selectMarketplaceItemVendorFn,
} from '../../store/selectors/marketplace';
import { TInitAddress } from '../../types/api';
import { ClaimSourceEnum, ShipsFromNoteVariantEnum } from '../../types/marketplace';
import { ICampaignItem } from '../../types/pyg';
import { DescriptionSourceEnum } from '../../types/shell';
import { ICustomOrFixedAddressForm } from '../../types/shipping';
import { IError, ISubmitAddressPayload } from '../../types/store';
import { AddressChoiceEnum } from '../../types/ui';
import { EMPTY_ADDRESS } from '../../util/address';
import { mapAddressInfo, mapContactInfo } from '../../util/form';
import { collectVendorNames, getMarketplaceItemId, isMarketplaceId } from '../../util/marketplace';
import { getShippingStepValidation } from '../../util/validation';
import { ShippingFormContainer } from './ShippingFormContainer';

import styles from './ShippingForm.module.scss';

const getItemKey = (item: ICampaignItem) => item.internalId;

const compactSummarySx: SxProps<Theme> = { display: 'flex', flexDirection: 'column', gap: 2 };
const flushTitleSx: SxProps<Theme> = { mb: 0 };

// Sticky rail: left border in place of a Divider element.
const asideSx: SxProps<Theme> = { alignSelf: 'stretch', borderLeft: 1, borderColor: 'divider', pl: 4 };

const stickyRailSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
  '& .MuiCard-root': { width: CARD_MAX_WIDTH, maxWidth: '100%' },
  '& .cl-card-media-row': { maxWidth: 360, maxHeight: 360, marginInline: 'auto' },
};

const ShippingContainer = () => {
  const { mobile } = useWindowSize();
  // Rail collapses to the compact summary card at RAIL_COLLAPSE_BREAKPOINT, not the app's `sm`.
  const isSingleColumn = useMediaQuery(`(max-width:${RAIL_COLLAPSE_BREAKPOINT - 0.02}px)`);
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();

  const isDigital = useSelector(selectIsDigital);
  const initialAddress = useSelector(selectInitialAddress);
  const items = useSelector(selectMappedPickedItems);
  const claimSource = useSelector(selectClaimSource);
  const campaignName = useSelector(selectSelectionCampaignName);
  const itemVendorFn = useSelector(selectMarketplaceItemVendorFn);
  const taxRequiredCountries = useSelector(selectTaxRequiredCountries);
  const allowedCountries = useSelector(selectAllowedCountries);

  const [openDrawer, setOpenDrawer] = useState(false);
  const [focusIndex, setFocusIndex] = useState<number | undefined>(undefined);

  const { fixedAddress, addressBook, initialValues } = useMemo(() => {
    const { fixed_address, address_book } = initialAddress || ({} as TInitAddress);

    const isFixedAddress = !!fixed_address?.length;
    const hasAnyAddress = !!(fixed_address?.length || address_book?.length);

    const contactValues = mapContactInfo(initialAddress);
    const addressValues = (() => {
      const nothing = {
        mode: AddressChoiceEnum.Select,
        picked_address_id: undefined,
        userAddress: undefined,
      };

      switch (true) {
        case isDigital:
          return nothing;
        case isFixedAddress && fixed_address.length === 1:
          return nothing;
        case isFixedAddress && fixed_address.length > 1:
          return { mode: AddressChoiceEnum.Select, userAddress: undefined, picked_address_id: '' };
        case !isFixedAddress && hasAnyAddress:
          // A single saved address shows no picker, so it is auto-selected; otherwise Confirm
          // would be permanently blocked with no way to satisfy validation.
          return {
            mode: AddressChoiceEnum.Select,
            picked_address_id: address_book?.length === 1 ? address_book[0].uid : '',
            userAddress: {
              ...EMPTY_ADDRESS,
              ...mapAddressInfo(initialAddress),
            },
          };
        case !isFixedAddress && !hasAnyAddress:
          return {
            mode: AddressChoiceEnum.Form,
            picked_address_id: undefined,
            userAddress: {
              ...EMPTY_ADDRESS,
              ...mapAddressInfo(initialAddress),
            },
          };
        default:
          console.warn('Unreachable default case in getting initialValues in ShippingContainer');

          return nothing;
      }
    })();

    const initValues: ICustomOrFixedAddressForm = {
      ...contactValues,
      ...addressValues,
    };

    return {
      fixedAddress: fixed_address,
      addressBook: address_book,
      initialValues: initValues,
    };
  }, [initialAddress, isDigital]);

  const validationSchema = useMemo(
    () =>
      getShippingStepValidation({
        isDigital,
        numberOfOptions: (fixedAddress || addressBook || []).length,
        isFixed: !!fixedAddress?.length,
        taxRequiredCountries,
        fixedAddresses: fixedAddress,
        addresses: addressBook,
        allowedCountries,
      }),
    [isDigital, fixedAddress, addressBook, taxRequiredCountries, allowedCountries],
  );

  const handleSubmit = useCallback(
    async (userInput: ICustomOrFixedAddressForm, actions: FormikHelpers<ICustomOrFixedAddressForm>) => {
      await new Promise((resolve, reject) => {
        const { mode, userAddress, picked_address_id, receiver_tax_number, ...contact } = userInput || {};

        if (!validationSchema.isValidSync(userInput)) {
          reject(new Error('Shipping validation error'));
          console.warn('Unreachable scenario. Please, contact administrator');

          return;
        }

        let values: ISubmitAddressPayload['values'] | undefined;

        switch (true) {
          // Digital or a single fixed address: nothing to change, so no address is sent.
          case isDigital || (fixedAddress && fixedAddress.length === 1): {
            values = {
              ...contact,
              ...(receiver_tax_number ? { receiver_tax_number } : {}),
            };
            break;
          }
          // Fixed address picked from several.
          case fixedAddress && fixedAddress.length > 1: {
            values = {
              ...contact,
              picked_address_id,
              ...(receiver_tax_number ? { receiver_tax_number } : {}),
            };
            break;
          }
          // Address picked from the address book.
          case mode === AddressChoiceEnum.Select && !!addressBook?.length: {
            values = {
              ...contact,
              picked_address_id,
              ...(receiver_tax_number ? { receiver_tax_number } : {}),
            };
            break;
          }
          // Address entered in the shipping form.
          case mode === AddressChoiceEnum.Form: {
            values = {
              ...contact,
              ...userAddress,
              ...(receiver_tax_number ? { receiver_tax_number } : {}),
            };
            break;
          }
          default: {
            values = {
              ...contact,
              ...userAddress,
              ...(receiver_tax_number ? { receiver_tax_number } : {}),
            };
          }
        }

        if (typeof values !== 'undefined') {
          dispatch(updateRequestAction({ values, resolve, reject }));
        } else {
          reject(
            new Error(
              'An error has occurred during the Shipping Address step submission. Please, contact administrator',
            ),
          );
          console.warn("Unreachable scenario - Shipping Form didn't return any values. Please, contact administrator");
        }
      })
        .then(() => {
          actions.setSubmitting(false);
        })
        .catch((e: IError | unknown) => {
          actions.setSubmitting(false);
          const { message } = e as IError;

          if (message) {
            toast.error(<div className={styles.toast} dangerouslySetInnerHTML={{ __html: message }} />);
          } else {
            toast.error(e as ToastContent);
          }
        })
        .then(() => trackEvent(trackFormSubmit(isDigital ? FORMS_ENUM.Digital : FORMS_ENUM.Physical)));
    },
    [dispatch, validationSchema, trackEvent, isDigital, fixedAddress, addressBook],
  );

  const handleOpenDrawer = useCallback(() => {
    setFocusIndex(undefined);
    setOpenDrawer(true);
  }, []);
  const handleOpenDrawerAt = useCallback((skuIndex: number) => {
    setFocusIndex(skuIndex);
    setOpenDrawer(true);
  }, []);
  const handleCloseDrawer = useCallback(() => setOpenDrawer(false), []);

  const detailsFooterButton = useMemo(() => {
    return (
      <Button variant="contained" color="primary" onClick={handleCloseDrawer} fullWidth={mobile}>
        Close
      </Button>
    );
  }, [mobile, handleCloseDrawer]);

  const isMarketplaceSelection = useMemo(
    () => items.length > 0 && items.every((item) => isMarketplaceId(getMarketplaceItemId(item))),
    [items],
  );

  const isBudgetClaim = claimSource === ClaimSourceEnum.MarketplaceBudget;

  const campaignTitle = useMemo(() => {
    if (campaignName) {
      return campaignName;
    }

    if (isBudgetClaim) {
      return getYourPicksTitle(items.length);
    }

    return items.length > 0 ? PICKED_FOR_YOU_TITLE : undefined;
  }, [campaignName, isBudgetClaim, items.length]);

  const marketplaceBrands = useMemo(
    () => (isMarketplaceSelection ? collectVendorNames(items, itemVendorFn) : []),
    [isMarketplaceSelection, items, itemVendorFn],
  );

  // A single product names its seller in the subtitle; a multi-item campaign reports the brands
  // in the shipping note instead.
  const isSingleMarketplaceItem = marketplaceBrands.length > 0 && items.length === 1;

  const shipsFromNote = useMemo(() => {
    if (!marketplaceBrands.length) {
      return null;
    }

    if (isSingleMarketplaceItem) {
      return <ShipsFromNote vendor={marketplaceBrands[0]} countryLabel={MARKETPLACE_SHIPPING_COUNTRY_LABEL} />;
    }

    return (
      <ShipsFromNote
        variant={ShipsFromNoteVariantEnum.Brands}
        brandCount={marketplaceBrands.length}
        vendor={marketplaceBrands[0]}
        countryLabel={MARKETPLACE_SHIPPING_COUNTRY_LABEL}
      />
    );
  }, [marketplaceBrands, isSingleMarketplaceItem]);

  const selectionCard = useMemo(
    () => (
      <Card
        items={items}
        title={campaignTitle}
        subtitle={isSingleMarketplaceItem ? <VendorLine vendor={marketplaceBrands[0]} /> : undefined}
        onSkuIndexClick={handleOpenDrawer}
        onSkuNameClick={handleOpenDrawerAt}
        footer={shipsFromNote}
        actions={
          <Button variant="outlined" color="primary" size="medium" fullWidth onClick={handleOpenDrawer}>
            View details
          </Button>
        }
      />
    ),
    [
      items,
      campaignTitle,
      isSingleMarketplaceItem,
      marketplaceBrands,
      shipsFromNote,
      handleOpenDrawer,
      handleOpenDrawerAt,
    ],
  );

  const renderDrawerItem = useCallback(
    (item: ICampaignItem) => (
      <DrawerItem
        item={item}
        descriptionSource={
          isMarketplaceId(getMarketplaceItemId(item))
            ? DescriptionSourceEnum.Marketplace
            : DescriptionSourceEnum.Inventory
        }
      />
    ),
    [],
  );

  const hasSelection = items.length > 0;
  const isCompactSummaryShown = hasSelection && isSingleColumn;
  const isStickyRailShown = hasSelection && !isSingleColumn;

  // Two columns (form flexes, rail auto-width) above the breakpoint; single column below.
  const appWrapperSx = useMemo<SxProps<Theme>>(() => {
    if (isSingleColumn) {
      return { flexDirection: 'column', ...(mobile && { pb: 2 }) };
    }

    return {
      display: 'grid',
      gridTemplateColumns: hasSelection ? 'minmax(0, 1fr) auto' : 'minmax(0, 1fr)',
      columnGap: 4,
      alignItems: 'start',
    };
  }, [isSingleColumn, mobile, hasSelection]);

  // Gap below the form on mobile/tablet so it clears the fixed footer.
  const formColumnSx = useMemo<SxProps<Theme>>(
    () => ({ display: 'flex', flexDirection: 'row', minWidth: 0, ...(isSingleColumn && { mb: 3 }) }),
    [isSingleColumn],
  );

  return (
    <AppWrapper sx={appWrapperSx}>
      {isCompactSummaryShown && (
        <Box sx={compactSummarySx}>
          <Title sx={flushTitleSx}>Your selection</Title>
          <SummaryCard title={campaignTitle ?? ''} items={items} onClick={handleOpenDrawer} />
        </Box>
      )}
      <Box sx={formColumnSx}>
        <Formik
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          validateOnChange
          validateOnBlur
          validateOnMount
        >
          <ShippingFormContainer addressBook={addressBook} fixedAddress={fixedAddress} />
        </Formik>
      </Box>
      {isStickyRailShown && (
        <Box component="aside" sx={asideSx}>
          <Box sx={stickyRailSx}>
            <Title sx={flushTitleSx}>Your selection</Title>
            {selectionCard}
          </Box>
        </Box>
      )}
      <ViewDetailsSidebar
        title={campaignTitle}
        open={openDrawer}
        onClose={handleCloseDrawer}
        items={items}
        renderItem={renderDrawerItem}
        getItemKey={getItemKey}
        footerButton={detailsFooterButton}
        focusIndex={focusIndex}
      />
    </AppWrapper>
  );
};

export default ShippingContainer;
